import { useCallback, useEffect, useRef, useState } from 'react';

import { useNavigate, useParams } from 'react-router-dom';

import { Button, Dropdown, FieldLabel, Modal, showSnackbar, TextArea, TextInput } from 'fluxo-ui';

import { AUTO_SAVE_INTERVAL, ROUTES, TIME_WARNINGS } from '../common/constants';
import { BlockLoading } from '../components/Loading';
import attemptService from '../services/attempt.service';
import examService from '../services/exam.service';
import questionFlagService, { FLAG_CATEGORY_LABELS, FlagCategory } from '../services/questionFlag.service';
import { useAttemptStore } from '../store/attemptStore';
import { useExamStore } from '../store/examStore';

const TakeExamPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { currentExam, setCurrentExam } = useExamStore();
    const { currentAttempt, startAttempt, resumeAttempt, saveAnswer, submitAttempt, getAnswer, toggleBookmark, markVisited } =
        useAttemptStore();

    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [timeRemaining, setTimeRemaining] = useState<number | null>(null);
    const [deadline, setDeadline] = useState<number | null>(null);
    const [showSubmitModal, setShowSubmitModal] = useState(false);
    const [selectedAnswer, setSelectedAnswer] = useState<any>(null);
    const [loading, setLoading] = useState(true);
    const [showPalette, setShowPalette] = useState(true);
    const [needsPasscode, setNeedsPasscode] = useState(false);
    const [passcode, setPasscode] = useState('');
    const [starting, setStarting] = useState(false);
    const [showFlagModal, setShowFlagModal] = useState(false);
    const [flagCategory, setFlagCategory] = useState<FlagCategory>('unclear');
    const [flagDescription, setFlagDescription] = useState('');
    const [flagSubmitting, setFlagSubmitting] = useState(false);

    const autoSubmittedRef = useRef(false);
    const warnedRef = useRef<Set<number>>(new Set());
    const answerRef = useRef<{ questionId: string; answer: any } | null>(null);
    const proctoringRef = useRef({ focusLossCount: 0, tabSwitchCount: 0, fullscreenViolations: 0, dirty: false });

    // Helper: extract populated question data from exam question entry
    const getQuestionData = useCallback((examQuestion: any) => {
        if (!examQuestion) return null;
        if (examQuestion.questionId && typeof examQuestion.questionId === 'object') {
            return examQuestion.questionId;
        }
        return examQuestion;
    }, []);

    // Helper: extract question ID string
    const getQuestionId = useCallback(
        (examQuestion: any) => {
            if (!examQuestion) return '';
            const qData = getQuestionData(examQuestion);
            return qData?._id || qData?.questionId || examQuestion.questionId || '';
        },
        [getQuestionData],
    );

    const beginAttempt = useCallback(
        async (exam: any, accessCode?: string) => {
            const attempt = await startAttempt(exam._id, accessCode);

            // Backend resumes an existing in-progress attempt when possible —
            // load its saved answers so nothing is lost on refresh
            if (attempt.resumed) {
                await resumeAttempt(attempt._id);
                showSnackbar('Resumed your in-progress attempt', 'Welcome back', { type: 'info' });
            }

            // Time remaining derives from the attempt start time, so a resumed
            // attempt continues its original countdown
            if (exam.duration) {
                const elapsedSeconds = Math.floor((Date.now() - new Date(attempt.startTime).getTime()) / 1000);
                const remaining = Math.max(0, exam.duration - elapsedSeconds);
                setTimeRemaining(remaining);
                setDeadline(Date.now() + remaining * 1000);
            }

            // Fullscreen enforcement
            if (exam.proctoring?.fullscreenRequired && document.documentElement.requestFullscreen) {
                document.documentElement.requestFullscreen().catch(() => {
                    showSnackbar('Please allow fullscreen mode for this exam', 'Fullscreen Required', { type: 'warning' });
                });
            }
        },
        [startAttempt, resumeAttempt],
    );

    // Fetch exam; gate on passcode when required
    useEffect(() => {
        const initialize = async () => {
            if (!id) {
                navigate(ROUTES.HOME);
                return;
            }

            try {
                const exam = await examService.getExam(id);
                setCurrentExam(exam);

                if ((exam as any).accessControl === 'passcode') {
                    setNeedsPasscode(true);
                    setLoading(false);
                    return;
                }

                await beginAttempt(exam);
                setLoading(false);
            } catch (error: any) {
                showSnackbar(error.message || 'Failed to load exam', 'Error', { type: 'error' });
                navigate(ROUTES.EXAMS);
            }
        };

        initialize();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    const handlePasscodeSubmit = async () => {
        if (!currentExam || !passcode.trim()) return;
        setStarting(true);
        try {
            await beginAttempt(currentExam, passcode.trim());
            setNeedsPasscode(false);
        } catch (error: any) {
            showSnackbar(error.message || 'Invalid access code', 'Access Denied', { type: 'error' });
        } finally {
            setStarting(false);
        }
    };

    // Timer: driven by an absolute deadline so it never drifts or stalls
    useEffect(() => {
        if (deadline === null) return;

        const timer = setInterval(() => {
            const remaining = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
            setTimeRemaining(remaining);

            if (TIME_WARNINGS.includes(remaining) && !warnedRef.current.has(remaining)) {
                warnedRef.current.add(remaining);
                showSnackbar(
                    remaining >= 60 ? `${Math.floor(remaining / 60)} minute(s) remaining` : `${remaining} seconds remaining`,
                    'Time Warning',
                    { type: 'warning' },
                );
            }

            if (remaining <= 0 && !autoSubmittedRef.current) {
                autoSubmittedRef.current = true;
                handleAutoSubmit();
            }
        }, 1000);

        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [deadline]);

    // Auto-save: stable interval reading the latest answer from a ref
    useEffect(() => {
        if (!currentAttempt) return;

        const autoSave = setInterval(() => {
            const pending = answerRef.current;
            if (pending && pending.answer !== null && pending.answer !== undefined) {
                saveAnswer({
                    questionId: pending.questionId,
                    answer: pending.answer,
                    timeSpent: AUTO_SAVE_INTERVAL,
                });
            }
        }, AUTO_SAVE_INTERVAL * 1000);

        return () => clearInterval(autoSave);
    }, [currentAttempt, saveAnswer]);

    // Proctoring: count tab switches / focus loss, sync periodically
    useEffect(() => {
        if (!currentAttempt || currentAttempt.status !== 'in_progress') return;
        const proctoring = (currentExam as any)?.proctoring || {};

        const onVisibility = () => {
            if (document.hidden) {
                proctoringRef.current.tabSwitchCount += 1;
                proctoringRef.current.dirty = true;
                if (proctoring.trackWindowFocus) {
                    showSnackbar('Tab switch detected — this is recorded', 'Proctoring', { type: 'warning' });
                }
            }
        };
        const onBlur = () => {
            proctoringRef.current.focusLossCount += 1;
            proctoringRef.current.dirty = true;
        };
        const onFullscreenChange = () => {
            if (proctoring.fullscreenRequired && !document.fullscreenElement) {
                proctoringRef.current.fullscreenViolations += 1;
                proctoringRef.current.dirty = true;
                showSnackbar('Fullscreen exit detected — this is recorded', 'Proctoring', { type: 'warning' });
            }
        };
        const blockClipboard = (e: Event) => {
            if (proctoring.disableCopyPaste) e.preventDefault();
        };
        const blockContextMenu = (e: Event) => {
            if (proctoring.disableRightClick || proctoring.disableCopyPaste) e.preventDefault();
        };

        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('blur', onBlur);
        document.addEventListener('fullscreenchange', onFullscreenChange);
        document.addEventListener('copy', blockClipboard);
        document.addEventListener('paste', blockClipboard);
        document.addEventListener('cut', blockClipboard);
        document.addEventListener('contextmenu', blockContextMenu);

        const sync = setInterval(() => {
            if (!proctoringRef.current.dirty) return;
            proctoringRef.current.dirty = false;
            const { focusLossCount, tabSwitchCount, fullscreenViolations } = proctoringRef.current;
            attemptService.updateProctoring(currentAttempt._id, { focusLossCount, tabSwitchCount, fullscreenViolations }).catch(() => {
                proctoringRef.current.dirty = true;
            });
        }, 15000);

        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('blur', onBlur);
            document.removeEventListener('fullscreenchange', onFullscreenChange);
            document.removeEventListener('copy', blockClipboard);
            document.removeEventListener('paste', blockClipboard);
            document.removeEventListener('cut', blockClipboard);
            document.removeEventListener('contextmenu', blockContextMenu);
            clearInterval(sync);
        };
    }, [currentAttempt, currentExam]);

    // Load saved answer when question changes
    useEffect(() => {
        if (!currentExam?.questions?.[currentQuestionIndex]) return;

        const examQ = currentExam.questions[currentQuestionIndex];
        const qId = getQuestionId(examQ);
        const savedAnswer = getAnswer(qId);
        setSelectedAnswer(savedAnswer?.answer ?? null);
        answerRef.current = null;
        markVisited(qId);
    }, [currentQuestionIndex, currentExam, getAnswer, markVisited, getQuestionId]);

    const flushProctoring = useCallback(async () => {
        const { focusLossCount, tabSwitchCount, fullscreenViolations, dirty } = proctoringRef.current;
        if (!dirty || !currentAttempt) return;
        try {
            await attemptService.updateProctoring(currentAttempt._id, { focusLossCount, tabSwitchCount, fullscreenViolations });
            proctoringRef.current.dirty = false;
        } catch {
            // best effort
        }
    }, [currentAttempt]);

    const finishAttempt = useCallback(
        async (auto: boolean) => {
            try {
                await flushProctoring();
                const { attempt } = await submitAttempt();
                if (document.fullscreenElement) {
                    document.exitFullscreen().catch(() => undefined);
                }
                showSnackbar(
                    auto ? 'Exam submitted automatically (time expired)' : 'Exam submitted successfully',
                    auto ? 'Submitted' : 'Success',
                    { type: auto ? 'info' : 'success' },
                );
                navigate(`/results/${attempt._id}`);
            } catch (error: any) {
                showSnackbar(error.message || 'Failed to submit exam', 'Error', { type: 'error' });
            }
        },
        [flushProctoring, submitAttempt, navigate],
    );

    const handleAutoSubmit = useCallback(() => finishAttempt(true), [finishAttempt]);
    const handleSubmit = () => finishAttempt(false);

    const handleFlagSubmit = async () => {
        const examQuestion = currentExam?.questions?.[currentQuestionIndex];
        if (!examQuestion || !flagDescription.trim()) {
            showSnackbar('Please describe the issue', 'Validation', { type: 'warning' });
            return;
        }
        setFlagSubmitting(true);
        try {
            await questionFlagService.createFlag({
                questionId: getQuestionId(examQuestion),
                category: flagCategory,
                description: flagDescription.trim(),
                examId: currentExam?._id,
                attemptId: currentAttempt?._id,
            });
            showSnackbar('Thanks — the issue has been reported to the reviewers', 'Reported', { type: 'success' });
            setShowFlagModal(false);
        } catch (error: any) {
            showSnackbar(error.message || 'Failed to report the issue', 'Error', { type: 'error' });
        } finally {
            setFlagSubmitting(false);
        }
    };

    const handleAnswerChange = (answer: any) => {
        setSelectedAnswer(answer);
        if (currentExam?.questions?.[currentQuestionIndex]) {
            const examQ = currentExam.questions[currentQuestionIndex];
            const questionId = getQuestionId(examQ);
            answerRef.current = { questionId, answer };
            saveAnswer({
                questionId,
                answer,
                timeSpent: 1,
            });
        }
    };

    const handleNext = () => {
        if (currentExam?.questions && currentQuestionIndex < currentExam.questions.length - 1) {
            setCurrentQuestionIndex((prev) => prev + 1);
        }
    };

    const handlePrevious = () => {
        if (currentQuestionIndex > 0) {
            setCurrentQuestionIndex((prev) => prev - 1);
        }
    };

    const formatTime = (seconds: number) => {
        const hours = Math.floor(seconds / 3600);
        const minutes = Math.floor((seconds % 3600) / 60);
        const secs = seconds % 60;
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    };

    const getQuestionStatus = (index: number) => {
        if (!currentExam?.questions?.[index]) return 'not-visited';
        const examQ = currentExam.questions[index];
        const qId = getQuestionId(examQ);
        const answer = getAnswer(qId);
        if (!answer) return 'not-visited';
        const hasAnswer = answer.answer !== null && answer.answer !== undefined;
        if (hasAnswer) return 'answered';
        if (answer.bookmarked) return 'bookmarked';
        if (answer.visited) return 'visited';
        return 'not-visited';
    };

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
                <BlockLoading text="Loading exam..." size="lg" />
            </div>
        );
    }

    // Passcode gate
    if (needsPasscode && currentExam) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-950 px-4">
                <div className="w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-700 p-8 space-y-5">
                    <div className="text-center">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-r from-teal-600 to-cyan-600 flex items-center justify-center mx-auto mb-4 shadow-md">
                            <span className="text-2xl">🔒</span>
                        </div>
                        <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white">{currentExam.title}</h1>
                        <p className="text-sm text-slate-500 dark:text-slate-400 mt-1.5">
                            This exam is protected. Enter the access code you received to begin.
                        </p>
                    </div>
                    <FieldLabel label="Access Code" required>
                        <TextInput
                            value={passcode}
                            onChange={(e) => setPasscode(e.value)}
                            placeholder="Enter access code"
                            disabled={starting}
                            onKeyPress={(e: any) => {
                                if (e.key === 'Enter') handlePasscodeSubmit();
                            }}
                        />
                    </FieldLabel>
                    <div className="flex gap-3">
                        <Button
                            label="Cancel"
                            variant="default"
                            layout="outlined"
                            className="flex-1"
                            onClick={() => navigate(ROUTES.EXAMS)}
                            disabled={starting}
                        />
                        <Button
                            label="Start Exam"
                            variant="primary"
                            className="flex-1"
                            onClick={handlePasscodeSubmit}
                            isLoading={starting}
                            disabled={starting || !passcode.trim()}
                        />
                    </div>
                </div>
            </div>
        );
    }

    if (!currentExam || !currentAttempt) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-50 dark:bg-slate-900">
                <BlockLoading text="Preparing your attempt..." size="lg" />
            </div>
        );
    }

    const currentExamQuestion = currentExam.questions?.[currentQuestionIndex];
    const currentQuestion = getQuestionData(currentExamQuestion);
    const totalQuestions = currentExam.questions?.length || 0;
    const answeredCount = currentExam.questions?.filter((_: any, i: number) => getQuestionStatus(i) === 'answered').length || 0;

    return (
        <div className="h-screen flex flex-col bg-slate-100 dark:bg-slate-900 overflow-hidden">
            {/* Exam Header — replaces app header */}
            <header className="bg-white dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 shadow-sm flex-shrink-0">
                <div className="px-4 py-2.5 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded bg-teal-600 flex items-center justify-center text-white font-bold text-sm">T</div>
                        <div>
                            <h1 className="text-base font-semibold text-slate-900 dark:text-white leading-tight">{currentExam.title}</h1>
                            <p className="text-xs text-slate-500 dark:text-slate-400">
                                Question {currentQuestionIndex + 1} of {totalQuestions} &middot; {answeredCount} answered
                            </p>
                        </div>
                    </div>
                    <div className="flex items-center gap-4">
                        {timeRemaining !== null && (
                            <div
                                className={`text-lg font-mono font-bold px-3 py-1 rounded ${
                                    timeRemaining < 300
                                        ? 'text-red-600 bg-red-50 dark:text-red-400 dark:bg-red-900/30'
                                        : 'text-teal-600 bg-teal-50 dark:text-teal-400 dark:bg-teal-900/30'
                                }`}
                            >
                                {formatTime(timeRemaining)}
                            </div>
                        )}
                        <button
                            onClick={() => setShowPalette(!showPalette)}
                            className="p-2 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400"
                            title="Toggle question palette"
                        >
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
                            </svg>
                        </button>
                        <Button label="Submit Exam" variant="primary" size="sm" onClick={() => setShowSubmitModal(true)} />
                    </div>
                </div>
                {/* Progress bar: answered / total */}
                <div className="h-1 bg-slate-200 dark:bg-slate-700">
                    <div
                        className="h-full bg-gradient-to-r from-teal-600 to-cyan-600 transition-all duration-300"
                        style={{ width: `${totalQuestions > 0 ? (answeredCount / totalQuestions) * 100 : 0}%` }}
                    />
                </div>
            </header>

            {/* Main content area */}
            <div className="flex-1 flex overflow-hidden">
                {/* Question area */}
                <div className="flex-1 overflow-y-auto">
                    <div className="max-w-3xl mx-auto px-4 py-5">
                        {/* Question card */}
                        <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700 p-5">
                            {currentQuestion && (
                                <div className="space-y-4">
                                    <div>
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300">
                                                Q{currentQuestionIndex + 1}
                                            </span>
                                            <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-teal-100 dark:bg-teal-900 text-teal-700 dark:text-teal-300">
                                                {currentQuestion.type === 'mcq_single'
                                                    ? 'Single Choice'
                                                    : currentQuestion.type === 'mcq_multiple'
                                                      ? 'Multiple Choice'
                                                      : currentQuestion.type === 'true_false'
                                                        ? 'True / False'
                                                        : currentQuestion.type === 'fill_blank'
                                                          ? 'Fill in the Blank'
                                                          : currentQuestion.type === 'short_answer'
                                                            ? 'Short Answer'
                                                            : currentQuestion.type === 'long_answer'
                                                              ? 'Long Answer'
                                                              : currentQuestion.type?.replace(/_/g, ' ')}
                                            </span>
                                            {currentExamQuestion?.marks && (
                                                <span className="text-xs text-slate-500 dark:text-slate-400 ml-auto">
                                                    {currentExamQuestion.marks} {currentExamQuestion.marks === 1 ? 'mark' : 'marks'}
                                                </span>
                                            )}
                                        </div>
                                        <h2 className="text-base font-semibold text-slate-900 dark:text-white leading-relaxed">
                                            {currentQuestion.title || currentQuestion.text}
                                        </h2>
                                        {currentQuestion.description && (
                                            <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{currentQuestion.description}</p>
                                        )}
                                    </div>

                                    {/* MCQ Single / True-False */}
                                    {(currentQuestion.type === 'mcq_single' || currentQuestion.type === 'true_false') &&
                                        (() => {
                                            const options =
                                                currentQuestion.type === 'true_false' &&
                                                (!currentQuestion.options || currentQuestion.options.length === 0)
                                                    ? [
                                                          { text: 'True', value: 'true' },
                                                          { text: 'False', value: 'false' },
                                                      ]
                                                    : currentQuestion.options || [];
                                            return (
                                                <div className="space-y-2">
                                                    {options.map((option: any, index: number) => {
                                                        const optValue = option.id || option.value || option.text || index;
                                                        const isSelected = selectedAnswer === optValue;
                                                        return (
                                                            <label
                                                                key={index}
                                                                className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                                                                    isSelected
                                                                        ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 dark:border-teal-400 ring-2 ring-teal-500/20 dark:ring-teal-400/20 shadow-sm'
                                                                        : 'border-slate-200 dark:border-slate-700 hover:border-teal-300 dark:hover:border-slate-500 hover:bg-slate-50 dark:hover:bg-slate-700/40'
                                                                }`}
                                                            >
                                                                <input
                                                                    type="radio"
                                                                    name="answer"
                                                                    checked={isSelected}
                                                                    onChange={() => handleAnswerChange(optValue)}
                                                                    className="mr-3 accent-teal-600"
                                                                />
                                                                <span className="text-sm text-slate-900 dark:text-white">
                                                                    {option.text || option.label}
                                                                </span>
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            );
                                        })()}

                                    {/* MCQ Multiple */}
                                    {currentQuestion.type === 'mcq_multiple' && (
                                        <div className="space-y-2">
                                            {currentQuestion.options?.map((option: any, index: number) => {
                                                const optValue = option.id || option.value || option.text || index;
                                                const isSelected = Array.isArray(selectedAnswer) && selectedAnswer.includes(optValue);
                                                return (
                                                    <label
                                                        key={index}
                                                        className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                                                            isSelected
                                                                ? 'border-teal-500 bg-teal-50 dark:bg-teal-900/20 dark:border-teal-400 ring-2 ring-teal-500/20 dark:ring-teal-400/20 shadow-sm'
                                                                : 'border-slate-200 dark:border-slate-700 hover:border-teal-300 dark:hover:border-slate-500 hover:bg-slate-50 dark:hover:bg-slate-700/40'
                                                        }`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={isSelected}
                                                            onChange={(e) => {
                                                                const prev = Array.isArray(selectedAnswer) ? [...selectedAnswer] : [];
                                                                if (e.target.checked) {
                                                                    prev.push(optValue);
                                                                } else {
                                                                    const idx = prev.indexOf(optValue);
                                                                    if (idx > -1) prev.splice(idx, 1);
                                                                }
                                                                handleAnswerChange(prev);
                                                            }}
                                                            className="mr-3 accent-teal-600"
                                                        />
                                                        <span className="text-sm text-slate-900 dark:text-white">
                                                            {option.text || option.label}
                                                        </span>
                                                    </label>
                                                );
                                            })}
                                            <p className="text-xs text-slate-500 dark:text-slate-400">Select all that apply</p>
                                        </div>
                                    )}

                                    {/* Fill in the blank */}
                                    {currentQuestion.type === 'fill_blank' && (
                                        <input
                                            type="text"
                                            value={selectedAnswer || ''}
                                            onChange={(e) => handleAnswerChange(e.target.value)}
                                            placeholder="Type your answer..."
                                            className="w-full px-3 py-2.5 border-2 border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:border-teal-500 dark:focus:border-teal-400 outline-none transition-colors text-sm"
                                        />
                                    )}

                                    {/* Short / Long answer / Code */}
                                    {(currentQuestion.type === 'short_answer' ||
                                        currentQuestion.type === 'long_answer' ||
                                        currentQuestion.type === 'code') && (
                                        <textarea
                                            value={selectedAnswer || ''}
                                            onChange={(e) => handleAnswerChange(e.target.value)}
                                            placeholder={currentQuestion.type === 'code' ? 'Write your code here...' : 'Type your answer...'}
                                            rows={currentQuestion.type === 'short_answer' ? 3 : 8}
                                            spellCheck={currentQuestion.type !== 'code'}
                                            className={`w-full px-3 py-2.5 border-2 border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:border-teal-500 dark:focus:border-teal-400 outline-none transition-colors resize-y text-sm ${
                                                currentQuestion.type === 'code' ? 'font-mono' : ''
                                            }`}
                                        />
                                    )}

                                    {/* Numerical */}
                                    {currentQuestion.type === 'numerical' && (
                                        <input
                                            type="number"
                                            value={selectedAnswer ?? ''}
                                            onChange={(e) =>
                                                handleAnswerChange(e.target.value === '' ? null : Number(e.target.value))
                                            }
                                            placeholder="Enter a number..."
                                            className="w-full px-3 py-2.5 border-2 border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:border-teal-500 dark:focus:border-teal-400 outline-none transition-colors text-sm"
                                        />
                                    )}

                                    {/* Fallback for any other question type */}
                                    {![
                                        'mcq_single',
                                        'mcq_multiple',
                                        'true_false',
                                        'fill_blank',
                                        'short_answer',
                                        'long_answer',
                                        'code',
                                        'numerical',
                                    ].includes(currentQuestion.type) && (
                                        <div className="space-y-2">
                                            <p className="text-xs text-amber-600 dark:text-amber-400">
                                                This question type is answered as free text and will be graded manually.
                                            </p>
                                            <textarea
                                                value={typeof selectedAnswer === 'string' ? selectedAnswer : ''}
                                                onChange={(e) => handleAnswerChange(e.target.value)}
                                                placeholder="Type your answer..."
                                                rows={5}
                                                className="w-full px-3 py-2.5 border-2 border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:border-teal-500 dark:focus:border-teal-400 outline-none transition-colors resize-y text-sm"
                                            />
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {/* Navigation buttons */}
                        <div className="mt-4 flex justify-between items-center">
                            <Button
                                label="Previous"
                                variant="default"
                                layout="outlined"
                                size="sm"
                                onClick={handlePrevious}
                                disabled={currentQuestionIndex === 0}
                            />
                            <div className="flex gap-2">
                                <Button
                                    label="Report Issue"
                                    variant="default"
                                    layout="outlined"
                                    size="sm"
                                    onClick={() => {
                                        setFlagCategory('unclear');
                                        setFlagDescription('');
                                        setShowFlagModal(true);
                                    }}
                                />
                                <Button
                                    label="Bookmark"
                                    variant="default"
                                    layout="outlined"
                                    size="sm"
                                    onClick={() => {
                                        if (currentExamQuestion) {
                                            toggleBookmark(getQuestionId(currentExamQuestion));
                                            showSnackbar('Question bookmarked', 'Info', { type: 'info' });
                                        }
                                    }}
                                />
                                {currentQuestionIndex < totalQuestions - 1 ? (
                                    <Button label="Next" variant="primary" size="sm" onClick={handleNext} />
                                ) : (
                                    <Button label="Submit Exam" variant="primary" size="sm" onClick={() => setShowSubmitModal(true)} />
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Question Palette sidebar */}
                {showPalette && (
                    <aside className="w-56 flex-shrink-0 bg-white dark:bg-slate-800 border-l border-slate-200 dark:border-slate-700 overflow-y-auto">
                        <div className="p-3">
                            <h3 className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-2">
                                Questions
                            </h3>
                            <div className="grid grid-cols-5 gap-1.5">
                                {currentExam.questions?.map((_: any, index: number) => {
                                    const status = getQuestionStatus(index);
                                    const isCurrent = index === currentQuestionIndex;
                                    let bgClass =
                                        'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-400 hover:bg-slate-200 dark:hover:bg-slate-600';
                                    if (isCurrent)
                                        bgClass = 'bg-gradient-to-r from-teal-600 to-cyan-600 text-white ring-2 ring-teal-300 dark:ring-teal-500/50 shadow-sm';
                                    else if (status === 'answered') bgClass = 'bg-green-500 text-white hover:bg-green-600';
                                    else if (status === 'bookmarked') bgClass = 'bg-amber-500 text-white hover:bg-amber-600';
                                    else if (status === 'visited')
                                        bgClass =
                                            'bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 hover:bg-red-200 dark:hover:bg-red-900/50';

                                    return (
                                        <button
                                            key={index}
                                            onClick={() => setCurrentQuestionIndex(index)}
                                            className={`w-8 h-8 rounded-lg text-xs font-semibold transition-colors ${bgClass}`}
                                        >
                                            {index + 1}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Legend */}
                            <div className="mt-4 space-y-1.5">
                                <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider">Legend</p>
                                <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                                    <span className="w-3 h-3 rounded bg-teal-600 inline-block"></span> Current
                                </div>
                                <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                                    <span className="w-3 h-3 rounded bg-green-500 inline-block"></span> Answered
                                </div>
                                <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                                    <span className="w-3 h-3 rounded bg-amber-500 inline-block"></span> Bookmarked
                                </div>
                                <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                                    <span className="w-3 h-3 rounded bg-red-100 dark:bg-red-900/30 inline-block"></span> Visited
                                    (unanswered)
                                </div>
                                <div className="flex items-center gap-2 text-xs text-slate-600 dark:text-slate-400">
                                    <span className="w-3 h-3 rounded bg-slate-100 dark:bg-slate-700 inline-block"></span> Not visited
                                </div>
                            </div>

                            {/* Summary */}
                            <div className="mt-4 pt-3 border-t border-slate-200 dark:border-slate-700">
                                <p className="text-xs text-slate-500 dark:text-slate-400">
                                    {answeredCount} of {totalQuestions} answered
                                </p>
                            </div>
                        </div>
                    </aside>
                )}
            </div>

            {/* Submit Confirmation Modal */}
            <Modal isOpen={showSubmitModal} onClose={() => setShowSubmitModal(false)} title="Submit Exam" size="sm">
                <div className="space-y-4">
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        Are you sure you want to submit your exam? You won't be able to change your answers after submission.
                    </p>
                    <div className="grid grid-cols-3 gap-2 text-center">
                        <div className="rounded-xl border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-900/40 p-3">
                            <div className="text-xl font-bold text-slate-900 dark:text-white">{totalQuestions}</div>
                            <div className="text-xs font-medium text-slate-500 dark:text-slate-400 mt-0.5">Total</div>
                        </div>
                        <div className="rounded-xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20 p-3">
                            <div className="text-xl font-bold text-green-600 dark:text-green-400">{answeredCount}</div>
                            <div className="text-xs font-medium text-green-600 dark:text-green-400 mt-0.5">Answered</div>
                        </div>
                        <div className="rounded-xl border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-3">
                            <div className="text-xl font-bold text-red-600 dark:text-red-400">{totalQuestions - answeredCount}</div>
                            <div className="text-xs font-medium text-red-600 dark:text-red-400 mt-0.5">Unanswered</div>
                        </div>
                    </div>
                    {totalQuestions - answeredCount > 0 && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg px-3 py-2">
                            You still have unanswered questions. They will be scored as unattempted.
                        </p>
                    )}
                    <div className="flex gap-3 justify-end">
                        <Button label="Cancel" variant="default" layout="outlined" size="sm" onClick={() => setShowSubmitModal(false)} />
                        <Button label="Submit" variant="primary" size="sm" onClick={handleSubmit} />
                    </div>
                </div>
            </Modal>

            {/* Report Issue Modal */}
            <Modal isOpen={showFlagModal} onClose={() => !flagSubmitting && setShowFlagModal(false)} title="Report an Issue" size="md">
                <div className="space-y-4 p-1">
                    <p className="text-sm text-slate-600 dark:text-slate-400">
                        Spotted a problem with this question? Your report goes to the reviewers — it will not affect your attempt.
                    </p>
                    <FieldLabel label="What kind of issue?" required>
                        <Dropdown
                            options={Object.entries(FLAG_CATEGORY_LABELS).map(([value, label]) => ({ label, value }))}
                            value={flagCategory}
                            onChange={(e) => setFlagCategory(e.value as FlagCategory)}
                            disabled={flagSubmitting}
                        />
                    </FieldLabel>
                    <FieldLabel label="Describe the issue" required>
                        <TextArea
                            value={flagDescription}
                            onChange={(e) => setFlagDescription(e.value)}
                            placeholder="e.g. Option B and C are both correct, but only one can be selected"
                            rows={4}
                            disabled={flagSubmitting}
                        />
                    </FieldLabel>
                    <div className="flex gap-3 justify-end">
                        <Button
                            label="Cancel"
                            variant="default"
                            layout="outlined"
                            size="sm"
                            onClick={() => setShowFlagModal(false)}
                            disabled={flagSubmitting}
                        />
                        <Button label="Send Report" variant="primary" size="sm" onClick={handleFlagSubmit} isLoading={flagSubmitting} />
                    </div>
                </div>
            </Modal>
        </div>
    );
};

export default TakeExamPage;
